import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Trash2 } from 'lucide-react';
import { bookingsAPI } from '../services/api';

const EditBookingModal = ({ booking, onClose, onSaved }) => {
  const isCricketMatch = booking?.serviceType === 'cricket_match';
  const hasTeamB = !!(booking?.teamDetails?.teamB?.name);

  const [form, setForm] = useState({
    booking_type: booking?.bookingType || 'single',
    team_a_name: booking?.teamDetails?.teamA?.name || '',
    team_a_captain_name: booking?.teamDetails?.teamA?.captain || '',
    team_a_captain_phone: booking?.teamDetails?.teamA?.phone || '',
    team_b_name: booking?.teamDetails?.teamB?.name || '',
    team_b_captain_name: booking?.teamDetails?.teamB?.captain || '',
    team_b_captain_phone: booking?.teamDetails?.teamB?.phone || '',
    contact_name: booking?.contactName || '',
    contact_phone: booking?.contactPhone || '',
    contact_email: booking?.contactEmail || '',
    total_amount: booking?.totalAmount ?? booking?.total_amount ?? 0,
    advance_amount: booking?.advanceAmount ?? booking?.advance_amount ?? 0,
    player_count: booking?.playerCount ?? booking?.player_count ?? 0,
  });
  const [removeTeamB, setRemoveTeamB] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const balance = Math.max(0, Number(form.total_amount) - Number(form.advance_amount));

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSave = async () => {
    if (!form.contact_name?.trim()) {
      setError('Contact name is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone?.trim() || null,
        contact_email: form.contact_email?.trim() || null,
        total_amount: Number(form.total_amount) || 0,
        advance_amount: Number(form.advance_amount) || 0,
        player_count: Number(form.player_count) || 0,
      };

      if (isCricketMatch) {
        payload.team_a_name = form.team_a_name?.trim() || null;
        payload.team_a_captain_name = form.team_a_captain_name?.trim() || null;
        payload.team_a_captain_phone = form.team_a_captain_phone?.trim() || null;

        if (removeTeamB) {
          payload.booking_type = 'single';
          payload.team_b_name = null;
          payload.team_b_captain_name = null;
          payload.team_b_captain_phone = null;
          payload.team_b_players = [];
        } else {
          payload.team_b_name = form.team_b_name?.trim() || null;
          payload.team_b_captain_name = form.team_b_captain_name?.trim() || null;
          payload.team_b_captain_phone = form.team_b_captain_phone?.trim() || null;
          payload.booking_type = form.team_b_name?.trim() ? 'both' : 'single';
        }
      }

      const bookingIds = booking.bookingIds || [booking.id];
      for (const id of bookingIds) {
        if (id) await bookingsAPI.update(id, payload);
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <h2 className="text-lg font-bold text-ga-text-primary">Edit Booking</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Booking reference badge */}
          <div className="flex items-center gap-2 text-sm text-ga-text-muted">
            <span className="bg-ga-primary/10 text-ga-primary px-2 py-0.5 rounded font-mono text-xs">
              {booking?.bookingReference || booking?.booking_reference || `#${booking?.id}`}
            </span>
            <span>{booking?.serviceName} · {booking?.timeSlot}</span>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Contact info */}
          <fieldset>
            <legend className="text-sm font-semibold text-ga-text-primary mb-2">Contact Information</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Name *</label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={e => handleChange('contact_name', e.target.value)}
                  className="ga-input w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Phone</label>
                <input
                  type="text"
                  value={form.contact_phone}
                  onChange={e => handleChange('contact_phone', e.target.value)}
                  className="ga-input w-full text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-ga-text-muted mb-1">Email</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={e => handleChange('contact_email', e.target.value)}
                  className="ga-input w-full text-sm"
                />
              </div>
            </div>
          </fieldset>

          {/* Team details (cricket match only) */}
          {isCricketMatch && (
            <fieldset>
              <legend className="text-sm font-semibold text-ga-text-primary mb-2">Team Details</legend>

              {/* Team A */}
              <div className="bg-blue-50 p-3 rounded-lg mb-3">
                <p className="text-xs font-semibold text-blue-800 mb-2">Team A</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Team name"
                    value={form.team_a_name}
                    onChange={e => handleChange('team_a_name', e.target.value)}
                    className="ga-input text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Captain name"
                    value={form.team_a_captain_name}
                    onChange={e => handleChange('team_a_captain_name', e.target.value)}
                    className="ga-input text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Captain phone"
                    value={form.team_a_captain_phone}
                    onChange={e => handleChange('team_a_captain_phone', e.target.value)}
                    className="ga-input text-sm sm:col-span-2"
                  />
                </div>
              </div>

              {/* Team B */}
              {!removeTeamB && (
                <div className="bg-green-50 p-3 rounded-lg mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-green-800">Team B</p>
                    {hasTeamB && (
                      <button
                        type="button"
                        onClick={() => setRemoveTeamB(true)}
                        className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 hover:underline"
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove Team B
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Team name"
                      value={form.team_b_name}
                      onChange={e => handleChange('team_b_name', e.target.value)}
                      className="ga-input text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Captain name"
                      value={form.team_b_captain_name}
                      onChange={e => handleChange('team_b_captain_name', e.target.value)}
                      className="ga-input text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Captain phone"
                      value={form.team_b_captain_phone}
                      onChange={e => handleChange('team_b_captain_phone', e.target.value)}
                      className="ga-input text-sm sm:col-span-2"
                    />
                  </div>
                </div>
              )}

              {removeTeamB && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 font-medium">Team B will be removed</p>
                    <p className="text-xs text-amber-700 mt-0.5">Booking type will change to single team.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRemoveTeamB(false)}
                    className="text-xs text-amber-700 underline hover:text-amber-900"
                  >
                    Undo
                  </button>
                </div>
              )}
            </fieldset>
          )}

          {/* Non-match: player count */}
          {!isCricketMatch && (
            <div>
              <label className="block text-xs text-ga-text-muted mb-1">Player Count</label>
              <input
                type="number"
                min="0"
                value={form.player_count}
                onChange={e => handleChange('player_count', e.target.value)}
                className="ga-input w-32 text-sm"
              />
            </div>
          )}

          {/* Amounts */}
          <fieldset>
            <legend className="text-sm font-semibold text-ga-text-primary mb-2">Payment</legend>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Total (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={form.total_amount}
                  onChange={e => handleChange('total_amount', e.target.value)}
                  className="ga-input w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Advance (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={form.advance_amount}
                  onChange={e => handleChange('advance_amount', e.target.value)}
                  className="ga-input w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-ga-text-muted mb-1">Balance (₹)</label>
                <div className="ga-input w-full text-sm bg-gray-50 flex items-center text-ga-text-muted">
                  {balance.toFixed(0)}
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-ga-text-muted hover:text-ga-text-primary hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-ga-primary hover:bg-ga-primary/90 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBookingModal;
